import { useEffect, useRef } from "react";
import { Box, Divider, Typography } from "@mui/joy";
import {
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Alert } from "../../../../shared";
import { newProjectQuickValidation } from "../../../../libs/form-validations";
import type { NewProjectQuickFormValues } from "../../../../libs/form-validations/new-project-quick";
import { useCreateProjectHandler } from "../NewProject/hooks";
import { quickDefaultValues } from "./variables";
import { useInitializeQuickForm } from "./hooks/useInitializeQuickForm";
import { mapQuickFormToCreatePayload } from "./utils/mapQuickFormToCreatePayload";
import { Action, Breadcrumb, CustomerStep, ProjectStep } from "./components";

function buildProjectNameFromCustomer(values: {
    customerName?: string;
    customerStreetAddress?: string;
    customerCity?: string;
    customerState?: string;
}): string {
    const customerName = (values.customerName || "").trim();
    const street = (values.customerStreetAddress || "").trim();
    const city = (values.customerCity || "").trim();
    const state = (values.customerState || "").trim();

    const locality = [city, state].filter(Boolean).join(", ");
    const address = [street, locality].filter(Boolean).join(", ");

    if (customerName && address) {
        return `${customerName} — ${address}`;
    }
    if (customerName) {
        return customerName;
    }
    if (address) {
        return address;
    }
    return "";
}

/** Keeps project name in sync with customer name/address until the user edits it. */
function AutoProjectNameSync() {
    const { control, setValue, getValues } =
        useFormContext<NewProjectQuickFormValues>();
    const lastAutoNameRef = useRef("");
    const manualEditRef = useRef(false);

    const customerName = useWatch({ control, name: "customerName" });
    const customerStreetAddress = useWatch({
        control,
        name: "customerStreetAddress",
    });
    const customerCity = useWatch({ control, name: "customerCity" });
    const customerState = useWatch({ control, name: "customerState" });
    const projectName = useWatch({ control, name: "name" });

    useEffect(() => {
        const currentName = (projectName || "").trim();
        const lastAuto = lastAutoNameRef.current.trim();

        if (!currentName) {
            manualEditRef.current = false;
            return;
        }

        if (lastAuto && currentName !== lastAuto) {
            manualEditRef.current = true;
        }
    }, [projectName]);

    useEffect(() => {
        const nextName = buildProjectNameFromCustomer({
            customerName,
            customerStreetAddress,
            customerCity,
            customerState,
        });

        if (manualEditRef.current) {
            return;
        }

        lastAutoNameRef.current = nextName;
        if (getValues("name") !== nextName) {
            setValue("name", nextName, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [
        customerName,
        customerStreetAddress,
        customerCity,
        customerState,
        getValues,
        setValue,
    ]);

    return null;
}

export default function NewProjectQuick() {
    const navigate = useNavigate();
    const { createProjectEventHandler, progress } = useCreateProjectHandler();
    const methods = useForm<NewProjectQuickFormValues>({
        mode: "onChange",
        defaultValues: quickDefaultValues,
        resolver: newProjectQuickValidation,
    });

    useInitializeQuickForm(methods.setValue, methods.getValues);

    const { handleSubmit } = methods;

    const onSubmit = async (data: NewProjectQuickFormValues) => {
        try {
            const payload = mapQuickFormToCreatePayload(data);
            const { projectId } = await createProjectEventHandler(payload);
            if (projectId) {
                if (String(projectId).startsWith("offline-")) {
                    navigate(`/?open=${encodeURIComponent(projectId)}`);
                } else {
                    navigate(`/project/list/${projectId}`);
                }
            }
        } catch (e) {
            console.error("Quick Project Creation Error:", e);
        }
    };

    return (
        <Box
            flex={1}
            sx={{
                width: "100%",
                boxSizing: "border-box",
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Breadcrumb />

            <Divider />

            <Box
                sx={{
                    flex: 1,
                    width: "100%",
                    minWidth: 0,
                    minHeight: 0,
                    overflow: "auto",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 700,
                        px: { xs: 2, sm: 3 },
                        pt: { xs: 2, sm: 3 },
                        pb: 4,
                        boxSizing: "border-box",
                    }}
                >
                    <Typography level="title-lg">Quick Project</Typography>
                    <Typography level="body-sm">
                        Enter customer and project details to create a project
                        quickly. Project name is suggested from the customer name
                        and address.
                    </Typography>

                    <FormProvider {...methods}>
                        <form
                            onSubmit={(event) => {
                                event.preventDefault();
                                void handleSubmit(onSubmit)();
                            }}
                        >
                            <AutoProjectNameSync />

                            <Box mt={2} sx={{ width: "100%", minWidth: 0 }}>
                                <Typography
                                    level="title-sm"
                                    sx={{ mb: 1.5, color: "text.secondary" }}
                                >
                                    Customer
                                </Typography>
                                <CustomerStep />

                                <Divider sx={{ my: 2.5 }} />

                                <Typography
                                    level="title-sm"
                                    sx={{ mb: 1.5, color: "text.secondary" }}
                                >
                                    Project
                                </Typography>
                                <ProjectStep />
                            </Box>

                            <Action
                                onCreate={() => void handleSubmit(onSubmit)()}
                                creating={progress.loading}
                            />

                            {progress.loading && (
                                <Typography sx={{ mt: 2 }} level="title-sm">
                                    {progress.message}
                                </Typography>
                            )}

                            {progress.error && (
                                <Box mt={2}>
                                    <Alert errorMessage={progress.error} />
                                </Box>
                            )}
                        </form>
                    </FormProvider>
                </Box>
            </Box>
        </Box>
    );
}
